import { useState, useEffect, useCallback } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { trackEvent } from "@lib/utils";

// Storage key for tracking if form has been submitted in this session
const FORM_SUBMITTED_KEY = "private_vault_form_submitted";

// Analytics event
const PRIVATE_VAULT_BANNER_SHOWN = "private_vault_banner_shown";

// UTM sources that should trigger the banner
export const ELIGIBLE_UTM_SOURCES = ["programmatic"];

// UTM campaigns that should trigger the banner
export const ELIGIBLE_UTM_CAMPAIGNS = [
  "ADRSBL_Display_ETH_WETH_LRTs_LSTs_10k_Global",
];

// Pages where the banner should be displayed
export const ELIGIBLE_PAGES = ["/gain/growth-vault"];

/**
 * Utility function to check if the private vault banner conditions are met.
 * Can be used outside of React components.
 */
export const checkPrivateVaultEligibility = (
  pathname: string | null,
  utmSource: string | null,
  utmCampaign: string | null,
): boolean => {
  const isEligiblePage = ELIGIBLE_PAGES.some((page) =>
    pathname?.startsWith(page),
  );

  const isEligibleUtmSource =
    utmSource &&
    ELIGIBLE_UTM_SOURCES.some(
      (source) => source.toLowerCase() === utmSource.toLowerCase(),
    );

  const isEligibleUtmCampaign =
    utmCampaign &&
    ELIGIBLE_UTM_CAMPAIGNS.some(
      (campaign) => campaign.toLowerCase() === utmCampaign.toLowerCase(),
    );

  return Boolean(
    isEligiblePage && isEligibleUtmSource && isEligibleUtmCampaign,
  );
};

type TUsePrivateVaultModalOptions = {
  /** Delay in milliseconds before showing the banner (default: 500ms for page load) */
  delay?: number;
  /** Custom UTM sources that trigger the banner */
  eligibleUtmSources?: string[];
  /** Custom UTM campaigns that trigger the banner */
  eligibleUtmCampaigns?: string[];
  /** Pages where the banner should be displayed (default: ["/gain/growth-vault"]) */
  eligiblePages?: string[];
};

type TUsePrivateVaultModalReturn = {
  /** Whether the banner should be visible */
  isVisible: boolean;
  /** Function to hide the banner completely */
  hideModal: () => void;
  /** Function to manually show the banner */
  showModal: () => void;
  /** Function to mark form as submitted (prevents banner from showing again) */
  markFormSubmitted: () => void;
  /** The detected UTM source (if any) */
  utmSource: string | null;
  /** The detected UTM campaign (if any) */
  utmCampaign: string | null;
  /** The trigger type (always "utm" since we only trigger on UTM) */
  triggerType: "utm" | null;
};

/**
 * Hook to manage Private Vault floating banner visibility based on UTM parameters.
 *
 * The banner will be shown when:
 * 1. User is on /gain/growth-vault page
 * 2. AND URL contains eligible utm_source (e.g., programmatic)
 * 3. AND URL contains eligible utm_campaign (e.g., ADRSBL_Display_ETH_WETH_LRTs_LSTs_10k_Global)
 * 4. AND the user has NOT submitted the form in this session
 *
 * The banner will only stop showing after user submits the form (clicks Connect now).
 */
const usePrivateVaultModal = (
  options: TUsePrivateVaultModalOptions = {},
): TUsePrivateVaultModalReturn => {
  const {
    delay = 500,
    eligibleUtmSources = ELIGIBLE_UTM_SOURCES,
    eligibleUtmCampaigns = ELIGIBLE_UTM_CAMPAIGNS,
    eligiblePages = ELIGIBLE_PAGES,
  } = options;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState(false);
  const [triggerType, setTriggerType] = useState<"utm" | null>(null);

  // Get UTM parameters from URL
  const utmSource = searchParams?.get("utm_source") || null;
  const utmCampaign = searchParams?.get("utm_campaign") || null;

  // Check if current page is eligible
  const isEligiblePage = eligiblePages.some((page) =>
    pathname?.startsWith(page),
  );

  // Check if UTM source is eligible
  const isEligibleUtmSource =
    utmSource &&
    eligibleUtmSources.some(
      (source) => source.toLowerCase() === utmSource.toLowerCase(),
    );

  // Check if UTM campaign is eligible
  const isEligibleUtmCampaign =
    utmCampaign &&
    eligibleUtmCampaigns.some(
      (campaign) => campaign.toLowerCase() === utmCampaign.toLowerCase(),
    );

  // Check if user has already submitted the form in this session
  const hasFormBeenSubmitted = useCallback(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(FORM_SUBMITTED_KEY) === "true";
  }, []);

  // Mark form as submitted in session storage (called after successful submission)
  const markFormSubmitted = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(FORM_SUBMITTED_KEY, "true");
    }
  }, []);

  // Show banner with tracking
  const showModal = useCallback(() => {
    // Don't show if form has already been submitted
    if (hasFormBeenSubmitted()) {
      return;
    }

    setIsVisible(true);
    setTriggerType("utm");

    trackEvent(PRIVATE_VAULT_BANNER_SHOWN, {
      trigger_type: "utm",
      utm_source: utmSource,
      utm_campaign: utmCampaign,
    });
  }, [hasFormBeenSubmitted, utmSource, utmCampaign]);

  // Hide banner completely
  const hideModal = useCallback(() => {
    setIsVisible(false);
  }, []);

  // Effect to handle UTM-based trigger - ONLY trigger for eligible UTM sources AND campaigns
  useEffect(() => {
    // Only trigger on eligible pages (/gain/growth-vault)
    if (!isEligiblePage) {
      return;
    }

    // Only trigger for eligible UTM sources (programmatic)
    if (!isEligibleUtmSource) {
      return;
    }

    // Only trigger for eligible UTM campaigns
    if (!isEligibleUtmCampaign) {
      return;
    }

    // Don't show if form already submitted
    if (hasFormBeenSubmitted()) {
      return;
    }

    // Small delay to ensure page is loaded
    const timer = setTimeout(() => {
      showModal();
    }, delay);

    return () => clearTimeout(timer);
  }, [
    delay,
    isEligiblePage,
    isEligibleUtmSource,
    isEligibleUtmCampaign,
    hasFormBeenSubmitted,
    showModal,
  ]);

  return {
    isVisible,
    hideModal,
    showModal,
    markFormSubmitted,
    utmSource,
    utmCampaign,
    triggerType,
  };
};

export default usePrivateVaultModal;
